/**
 * Landing Layout Component
 *
 * Shared layout wrapper for all public-facing pages (landing, about, pricing, etc.).
 * Provides consistent header navigation, footer, and mobile-responsive menu.
 *
 * @module components/layout/landing-layout
 *
 * @remarks
 * Features:
 * - Fixed header with glassmorphism effect (backdrop-blur)
 * - Responsive navigation: horizontal on desktop, hamburger menu on mobile
 * - Footer with product links, support contacts, and social media
 * - Theme-aware styling with amber/orange accents
 *
 * Mobile Menu Behavior:
 * - Hidden on screens >= 640px (sm breakpoint)
 * - Hamburger icon toggles dropdown menu
 * - Menu closes automatically when navigating
 *
 * @example
 * // Route definition in App.tsx (wraps public routes)
 * <Route element={<LandingLayout />}>
 *   <Route path="/" element={<LandingPage />} />
 *   <Route path="/about" element={<AboutPage />} />
 *   <Route path="/pricing" element={<PricingPage />} />
 * </Route>
 */

import { useState, useCallback, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/client/hooks/use-auth';
import { STEALTH_MODE } from '@/client/lib/config';

/**
 * Force light mode for public pages.
 * Removes 'dark' class from document when mounted, restores on unmount.
 */
function useForceLightMode() {
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');

    // Force light mode
    root.classList.remove('dark');

    return () => {
      // Restore dark mode if it was previously active
      if (wasDark) {
        root.classList.add('dark');
      }
    };
  }, []);
}

/**
 * Layout component for public-facing pages with header, footer, and mobile menu.
 *
 * @returns {JSX.Element} The rendered layout with header, main content (Outlet), and footer
 */
export function LandingLayout() {
  // Force light mode on all public pages
  useForceLightMode();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const handleRequestAccess = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setMobileMenuOpen(false);

      // If we're already on the home page, scroll to the section
      if (location.pathname === '/') {
        const element = document.getElementById('request-access');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home page with hash
        navigate('/', { state: { scrollTo: 'request-access' } });
        // Give time for navigation, then scroll
        setTimeout(() => {
          const element = document.getElementById('request-access');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    },
    [location.pathname, navigate]
  );

  const handleLogoClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setMobileMenuOpen(false);
      navigate('/');
      // Always scroll to top when clicking logo
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate]
  );

  return (
    <div className="grid-main-content bg-background">
      {/* Header with view transition */}
      <header className="vt-header fixed top-0 left-0 right-0 z-50 border-b border-border bg-card/95 backdrop-blur-xl shadow-sm safe-area-x">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link
            to="/"
            onClick={handleLogoClick}
            className="flex items-center gap-2.5 font-bold text-xl group cursor-pointer"
          >
            <img
              src="/assets/logo.png"
              alt="Armenian Link"
              className="h-8 w-8 rounded-lg group-hover:scale-110 transition-transform"
            />
            <span className="text-foreground">Armenian Link</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-2">
            {!STEALTH_MODE && (
              <>
                <Link
                  to="/features"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Features
                </Link>
                <Link
                  to="/pricing"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  About
                </Link>
              </>
            )}
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-4 py-2 text-sm font-semibold text-zinc-900 transition-all hover:scale-105"
              >
                <LayoutDashboard className="w-4 h-4" />
                My Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Sign in
                </Link>
                <button
                  onClick={handleRequestAccess}
                  className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-4 py-2 text-sm font-semibold text-zinc-900 transition-all hover:scale-105"
                >
                  Request Access
                </button>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-border bg-card backdrop-blur-xl shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-3">
              {!STEALTH_MODE && (
                <>
                  <Link
                    to="/features"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Features
                  </Link>
                  <Link
                    to="/pricing"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/about"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    About
                  </Link>
                </>
              )}
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-4 py-3 text-sm font-semibold text-zinc-900 transition-all"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  My Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Sign in
                  </Link>
                  <button
                    onClick={handleRequestAccess}
                    className="block w-full text-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-4 py-3 text-sm font-semibold text-zinc-900 transition-all"
                  >
                    Request Access
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16 shrink-0" aria-hidden="true" />

      {/* Main content with view transition */}
      <main className="vt-main overflow-y-auto overflow-x-hidden">
        <Outlet />
      </main>

      {/* Footer - CSS Grid for responsive columns */}
      <footer className="relative border-t border-border bg-background shrink-0 safe-area-x safe-area-bottom">
        <div className="container mx-auto section-padding">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-12">
            {/* Brand */}
            <div>
              <Link
                to="/"
                onClick={handleLogoClick}
                className="flex items-center gap-2.5 font-bold text-lg mb-4 cursor-pointer"
              >
                <img src="/assets/logo.png" alt="Armenian Link" className="h-7 w-7 rounded-lg" />
                <span className="text-foreground">Armenian Link</span>
              </Link>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Links, QR codes, pages, and forms — built for the Armenian community.
              </p>
              <p className="text-sm text-muted-foreground">
                Questions?{' '}
                <a
                  href="mailto:hello@armenian.link"
                  className="text-primary hover:text-primary/80 transition-colors"
                >
                  hello@armenian.link
                </a>
              </p>
            </div>

            {/* Product - hidden in stealth mode */}
            {!STEALTH_MODE && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-4">Product</h4>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/features"
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Features
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/pricing"
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      About
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar - Flexbox micro-layout with safe centering */}
          <div className="pt-8 border-t border-border flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              &copy; {new Date().getFullYear()} Armenian Link. Made with pride.
            </p>
            <p className="text-sm text-muted-foreground text-center sm:text-right">
              An indie project by a solo dev
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
